/**
 * The closed set of client-side routes the help chat agent is allowed to be told about
 * (`BACKLOG.md` B6a). Part of the compliance boundary alongside `topics.ts`/`topicMatcher.ts`:
 * the chat request body carries a `route` field so the agent can be a little more specific ("you're
 * looking at the Access page"), and that field has to be validated against a closed, known set
 * rather than accepted as an arbitrary client-supplied string — an open string field is exactly the
 * kind of thing a future call site could widen into "also pass whatever's in the URL", which is how
 * a patient id in a path segment (`/patients/:id`, once that screen exists — see `patients-and-
 * scheduling` in `topics.ts`) would end up reaching the LLM's context by accident rather than by
 * anyone deciding it should.
 *
 * Mirrors `web/src/shell/navigation.ts`'s `NAV_ITEMS` paths plus the two routes outside the
 * authenticated shell (`/login`) and the help page itself (`/help`) — kept as a separate, independent
 * list rather than importing `NAV_ITEMS` (a `web/`-only module `server/` cannot import), so this is
 * intentionally a second declaration of the same route strings, not a shared one; keep it in sync by
 * hand when a route is added or renamed.
 */

export const HELP_ROUTES = ['/', '/access', '/analytics', '/audit', '/login', '/help'] as const;

export type HelpRoute = (typeof HELP_ROUTES)[number];

const HELP_ROUTE_SET: ReadonlySet<string> = new Set(HELP_ROUTES);

export function isHelpRoute(value: string): value is HelpRoute {
  return HELP_ROUTE_SET.has(value);
}
